import { MapPin, Mail, Phone, Users, Award, Target, Wrench, BarChart3, Cpu, GraduationCap } from 'lucide-react';

// Facility image
const FACILITY_IMAGE = 'https://images.unsplash.com/photo-1547394765-185e1e68f34e?w=1200&q=80';
const RACING_IMAGE = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80';

const stats = [
  { value: '2.75km', label: 'Main Circuit Length' },
  { value: '12', label: 'Technical Corners' },
  { value: '100+', label: 'Teams Served' },
  { value: '500+', label: 'Events Per Year' },
];

const stemPrograms = [
  {
    icon: Wrench,
    title: 'Motorsport Engineering',
    color: 'blue',
    description: 'Learn vehicle engineering, aerodynamics, and race car design fundamentals.',
    duration: '8 weeks',
    ageGroup: 'Years 9-12',
  },
  {
    icon: BarChart3,
    title: 'Data Analysis in Racing',
    color: 'green',
    description: 'Explore how teams use data to improve performance and make strategic decisions.',
    duration: '6 weeks',
    ageGroup: 'Years 10-12',
  },
  {
    icon: Cpu,
    title: 'Automotive Electronics',
    color: 'purple',
    description: 'Dive into the electronic systems that power modern race cars.',
    duration: '10 weeks',
    ageGroup: 'Years 11-12',
  },
  {
    icon: GraduationCap,
    title: 'Career Pathways',
    color: 'orange',
    description: 'Discover diverse career opportunities in the motorsport industry.',
    duration: '4 weeks',
    ageGroup: 'Years 9-12',
  },
];

const team = [
  {
    name: 'Peter Davidson',
    role: 'Chief Executive Officer',
    initials: 'PD',
    bio: 'Peter brings over 20 years of motorsport management experience to DriveIT NQ.',
  },
  {
    name: 'Sarah Mitchell',
    role: 'Operations Manager',
    initials: 'SM',
    bio: 'Sarah oversees daily operations and ensures every team has an exceptional experience.',
  },
  {
    name: 'Michael Chen',
    role: 'Technical Director',
    initials: 'MC',
    bio: 'Michael leads our technical team and maintains our FIA certification standards.',
  },
];

const colorClasses = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
};

export function AboutPage() {
  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Hero */}
        <div className="relative rounded-3xl overflow-hidden mb-16">
          <img
            src={FACILITY_IMAGE}
            alt="DriveIT NQ Facility"
            className="w-full h-80 md:h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-2xl px-8">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">About DriveIT NQ</h1>
              <p className="text-xl text-gray-200">
                North Queensland's premier motorsport facility, dedicated to providing
                world-class racing experiences and developing the next generation of
                motorsport talent.
              </p>
            </div>
          </div>
        </div>

        {/* Mission */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Target className="text-red-600" size={24} />
                <span className="text-red-600 font-semibold uppercase tracking-wide text-sm">Our Mission</span>
              </div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">
                Bringing World-Class Motorsport to North Queensland
              </h2>
              <p className="text-gray-600 mb-4 text-lg">
                DriveIT NQ was established to bring world-class motorsport facilities
                to North Queensland. Our FIA-certified circuit, professional drag
                strip, and training facilities serve everyone from professional
                racing teams to enthusiastic amateurs.
              </p>
              <p className="text-gray-600 text-lg">
                Beyond racing, we're committed to inspiring the next generation
                through our STEM education programs, showing young people the
                exciting career opportunities in motorsport engineering, data
                analysis, and automotive technology.
              </p>
            </div>
            <div className="relative">
              <img
                src={RACING_IMAGE}
                alt="Racing at DriveIT NQ"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">Est. 2018</div>
                <div className="text-red-200">Serving the community</div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mb-16">
          <div className="bg-gray-900 rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-red-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STEM Programs */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Award className="text-red-600" size={24} />
              <span className="text-red-600 font-semibold uppercase tracking-wide text-sm">Education</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">STEM Education Programs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Inspiring the next generation of motorsport professionals through hands-on
              learning experiences.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stemPrograms.map((program) => {
              const colors = colorClasses[program.color as keyof typeof colorClasses];
              const Icon = program.icon;

              return (
                <div key={program.title} className="card p-6">
                  <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={colors.text} size={24} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{program.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{program.description}</p>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{program.duration}</span>
                    <span>{program.ageGroup}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Team */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Users className="text-red-600" size={24} />
              <span className="text-red-600 font-semibold uppercase tracking-wide text-sm">Our Team</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet the Leadership</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our experienced team is dedicated to delivering exceptional motorsport experiences.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member) => (
              <div key={member.name} className="card p-6 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-gray-500">{member.initials}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-lg">{member.name}</h3>
                <p className="text-red-600 text-sm mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section>
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Contact Us</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-red-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
                <p className="text-gray-600">
                  123 Raceway Drive<br />
                  Townsville QLD 4810<br />
                  Australia
                </p>
              </div>

              <div className="text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-red-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a href="mailto:info@driveitnq.com.au" className="text-gray-600 hover:text-red-600">
                  info@driveitnq.com.au
                </a>
              </div>

              <div className="text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-red-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a href="tel:0740000000" className="text-gray-600 hover:text-red-600">
                  07 4000 0000
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
